import { Badge, Box, Flex, Heading, Text } from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import { useApi } from "hooks/useApi";
import { useCache } from "hooks/useCache";
import { useMemo, useState } from "react";
import { statusColors } from "typing";
import DashboardTable from "../DashboardTable";

type Table3RowType = {
  id: string;
  name: string;
  sub_area_name: string;
  coverage: string;
  total_collected: any;
  success_rate: any;
  last_activity: any;
  last_scan_time: any;
  status: any;
};

export default function TaxCollectorsTable(props: any) {
  const { numberOfInactiveCollectors } = props;
  const [activeStatus, setActiveStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [searchBarValue34, setSearchBarValue34] = useState("");
  const { getTaxCollectors } = useApi();
  const { data: taxCollectors, mutate } = useCache("getTaxCollectors", () =>
    getTaxCollectors(page, searchBarValue34, 5, activeStatus)
  );
  // console.log(taxCollectors)
  const handleSearch = (event: any) => {
    const value = event.target.value;
    setSearchBarValue34(value);
    mutate(getTaxCollectors(page, value, 5));
  };
  const handleClearSearch = (event: any) => {
    setSearchBarValue34("");
    mutate(getTaxCollectors(page, ""));
  };

  const handleJumpToPage = (pg: number) => {
    setPage(pg);
    mutate(getTaxCollectors(pg));
  };
  const handleSetNextPage = () => {
    if (
      taxCollectors?.length > 0 &&
      taxCollectors[0].num_of_pages >= page + 1
    ) {
      setPage((prev: any) => prev + 1);
      mutate(getTaxCollectors(page + 1));
    }
  };
  const handleSetPrevPage = () => {
    if (taxCollectors?.length > 0 && page > 0) {
      setPage((prev: any) => prev - 1);
      mutate(getTaxCollectors(page + 1));
    }
  };
  const table3Columns = useMemo(() => {
    const table3ColumnHelper = createColumnHelper<Table3RowType>();
    return [
      table3ColumnHelper.accessor("name", {
        cell: (info) => (
          <Box flex={1}>
            <Flex gap="8px" alignItems="center" px="12px" py="0px">
              <Text style={{ textTransform: "capitalize" }}>
                {info.getValue<string>()}
              </Text>
            </Flex>
          </Box>
        ),
        header: (info) => (
          <Flex flex={1} px={"25px"} py="14px">
            <Heading as="h6">Nom</Heading>
          </Flex>
        ),
        meta: { width: "100px" },
      }),
      table3ColumnHelper.accessor("sub_area_name", {
        cell: (info) => (
          <Box flex={1}>
            <Flex gap="8px" alignItems="center" px="12px" py="0px">
              <Text style={{ textTransform: "capitalize" }}>
                {info.getValue<string>()}
              </Text>
            </Flex>
          </Box>
        ),
        header: (info) => (
          <Flex flex={1} px={"25px"} py="14px">
            <Heading as="h6">Zone</Heading>
          </Flex>
        ),
        meta: { width: "100px" },
      }),
      table3ColumnHelper.accessor("coverage", {
        cell: (info) => (
          <Box flex={1}>
            <Flex gap="8px" alignItems="center" px="12px" py="0px">
              <Text style={{ textTransform: "capitalize" }}>
                {info.getValue<string>()}%
              </Text>
            </Flex>
          </Box>
        ),
        header: (info) => (
          <Flex flex={1} px={"25px"} py="14px">
            <Heading as="h6">Couverture</Heading>
          </Flex>
        ),
        meta: { width: "100px" },
      }),
      table3ColumnHelper.accessor("total_collected", {
        cell: (info) => (
          <Box flex={1}>
            <Flex gap="8px" alignItems="center" px="12px" py="0px">
              <Text style={{ textTransform: "capitalize" }}>
                {info.getValue<string>()} FCFA
              </Text>
            </Flex>
          </Box>
        ),
        header: (info) => (
          <Flex flex={1} px={"25px"} py="14px">
            <Heading as="h6">Total collecté</Heading>
          </Flex>
        ),
        meta: { width: "120px" },
      }),
      table3ColumnHelper.accessor("last_scan_time", {
        cell: (info) => (
          <Box flex={1}>
            <Flex
              gap="8px"
              alignItems="center"
              px="12px"
              py="0px"
              justifyContent={"flex-end"}
            >
              <Text style={{ textTransform: "capitalize" }}>
                {info.getValue<string>()}
              </Text>
            </Flex>
          </Box>
        ),
        header: (info) => (
          <Flex flex={1} px={"25px"} py="14px" justifyContent={"flex-end"}>
            <Heading as="h6">Dernière activité</Heading>
          </Flex>
        ),
        meta: { width: "130px" },
      }),
      table3ColumnHelper.accessor("status", {
        cell: (info) => (
          <Box flex={1}>
            <Flex
              gap="8px"
              alignItems="center"
              px="12px"
              py="0px"
              justifyContent={"flex-end"}
            >
              <Badge
                py={1.5}
                px={2}
                colorScheme={statusColors[info.getValue<string>()]}
                fontSize={"12px"}
                rounded={10}
                width={"80px"}
                style={{
                  fontWeight: "lighter",
                  justifyContent: "center",
                  alignItems: "center",
                  display: "flex",
                  textTransform: "capitalize",
                }}
              >
                {info.getValue<string>()}
              </Badge>
            </Flex>
          </Box>
        ),
        header: (info) => (
          <Flex flex={1} px={"25px"} py="14px" justifyContent={"flex-end"}>
            <Heading as="h6">Statut</Heading>
          </Flex>
        ),
        meta: { width: "100px" },
      }),
    ];
  }, []);
  return (
    <>
      <Flex
        w="100%"
        flexDirection="row"
        justifyContent={"space-between"}
        alignItems={"center"}
        bg={"white"}
        p={"10px"}
        gap={"10px"}
        borderTopRadius={"10px"}
        py={"20px"}
      >
        <Flex justifyContent={"flex-start"} alignItems="center">
          <Text fontSize="16px" fontWeight="bold">
            Suivi des collecteurs
          </Text>
          {numberOfInactiveCollectors && (
            <Badge
              ml={2}
              colorScheme="red"
              fontSize="14px"
              rounded="full"
              px={2}
              py={1}
            >
              {numberOfInactiveCollectors} inactif(s)
            </Badge>
          )}
        </Flex>
        <Flex gap="10px" alignItems="center">
          <input
            type="checkbox"
            onChange={(e) => {
              setActiveStatus(e.target.checked ? "active" : "inactive");
              if (e.target.checked) {
                mutate(getTaxCollectors(page, searchBarValue34, 5, "active"));
              } else {
                mutate(getTaxCollectors(page, searchBarValue34, 5, "inactive"));
              }
            }}
          />
          <Text ml={2} fontSize="14px">
            Actifs uniquement
          </Text>
        </Flex>
      </Flex>
      <DashboardTable
        title={``}
        pageSize={pageSize}
        setPageSize={setPageSize}
        page={page}
        handleJumpToPage={handleJumpToPage}
        handleSearch={handleSearch}
        handleSetNextPage={handleSetNextPage}
        handleSetPrevPage={handleSetPrevPage}
        handleClearSearch={handleClearSearch}
        tableColumns={table3Columns}
        tableData={taxCollectors}
        searchKey={searchBarValue34}
        setSearchKey={setSearchBarValue34}
      />
    </>
  );
}
